import { ChangeDetectionStrategy, Component, computed, effect, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { ContactRole, OrganizationService } from "../../../services/organization.service";
import { S25EmailComponent } from "../../s25-email/s25.email.component";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";

@TypeManagerDecorator("s25-ng-se-email-compose")
@Component({
    selector: "s25-ng-se-email-compose",
    host: { class: "email-compose" },
    template: `
        <div class="org-picker">
            <label class="picker-label">Send to organizations</label>
            <s25-ng-multiselect-search-criteria
                [type]="'organizations'"
                [buttonText]="'Select Organizations'"
                [selectedItems]="selectedOrgs()"
                (close)="onOrgsChanged($event)"
                (onDone)="onOrgsDone($event)"
            />
            @if (selectedOrgs().length) {
                <p class="sidebar-recipients">
                    Selected :
                    @for (org of selectedOrgs(); track org.itemId; let last = $last) {
                        <strong>{{ org.itemName }}{{ last ? "" : ", " }}</strong>
                    }
                </p>
            }
            @if (isLoadingMembers()) {
                <s25-ng-loading-inline-static />
            }
        </div>

        <div class="email-container">
            @if (showEmailForm()) {
                <s25-ng-email
                    [contextId]="0"
                    [allowCustomFrom]="true"
                    [orgMembers]="bulkMembers()"
                    [showFormAssignment]="true"
                    [showCloseButton]="false"
                    (closed)="onEmailClosed()"
                />
            }
        </div>
    `,
    styles: `
        :host {
            display: block;
        }

        .org-picker {
            margin-bottom: 1.5rem;
        }

        .picker-label {
            display: block;
            font-weight: 600;
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
            color: var(--color--text, #333);
        }

        .sidebar-recipients {
            margin: 0.5rem 0 0;
            font-size: 13px;
            color: var(--color--text-muted, #6b7280);
            line-height: 1.5;
        }

        .email-container {
            border: 1px solid var(--color--border, #e5e5e5);
            border-radius: 6px;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25EmailComponent, S25MultiselectModule, S25LoadingInlineModule],
})
export class SeEmailComposeComponent {
    readonly selectedOrgs = signal<S25ItemI[]>([]);
    readonly showEmailForm = signal(true);

    // Updated only on (onDone) so the resource fetches once per commit, not on every click.
    private readonly committedOrgs = signal<S25ItemI[]>([]);

    private readonly membersResource = s25Resource<ContactRole[], number[]>({
        if: () => this.committedOrgs().length > 0,
        params: () => this.committedOrgs().map((org) => org.itemId as number),
        loader: ({ params: ids }) => OrganizationService.getContactsForOrgs(ids),
    });

    readonly isLoadingMembers = this.membersResource.isLoading;
    readonly bulkMembers = computed(() => this.membersResource.value() ?? []);

    constructor() {
        effect(() => {
            const loadError = this.membersResource.error();
            if (loadError) S25Util.showError(loadError);
        });
    }

    onEmailClosed() {
        this.showEmailForm.set(false);
        setTimeout(() => this.showEmailForm.set(true));
    }

    onOrgsChanged(orgs: S25ItemI[]) {
        this.selectedOrgs.set(orgs);
    }

    onOrgsDone(orgs: S25ItemI[]) {
        this.selectedOrgs.set(orgs);
        this.committedOrgs.set(orgs);
    }
}
